import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Microscope, Target, Brain, Users, Globe, BarChart3,
  ArrowRight, CheckCircle2, Lightbulb, TrendingUp
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

export default function ResearchOverview() {
  const methodology = [
    {
      title: "Diagnostic Instruments",
      description: "Validated assessment tools measuring workforce intelligence dimensions with statistical reliability."
    },
    {
      title: "Longitudinal Studies",
      description: "Tracking workforce indicators over time to identify trends and predictive patterns."
    },
    {
      title: "Industry Benchmarking",
      description: "Cross-sector analysis enabling comparative insights across industries and regions."
    },
    {
      title: "AI-Powered Analytics",
      description: "Machine learning models for pattern recognition and predictive workforce intelligence."
    }
  ];

  const focusAreas = [
    { icon: Users, title: "Workforce Intelligence", description: "Trust, alignment, readiness, AI capability, and resilience measurement." },
    { icon: BarChart3, title: "Leadership Effectiveness", description: "Leadership behaviors and their impact on workforce outcomes." },
    { icon: TrendingUp, title: "Service Quality", description: "Service delivery models and operational quality metrics." },
    { icon: Brain, title: "AI & Human Capability", description: "Integration of AI with human workforce development." },
    { icon: Lightbulb, title: "Workforce Readiness", description: "Organizational preparedness for transformation." },
    { icon: Globe, title: "Economic Development", description: "Workforce intelligence as economic growth driver." }
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="research-overview-page">
      {/* Hero */}
      <section className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link to="/research" className="inline-flex items-center gap-2 text-[#1E3A8A] hover:text-[#991B1B] mb-6 text-sm">
              <ArrowRight className="w-4 h-4 rotate-180" />
              Back to Research
            </Link>
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1E3A8A]/10 text-[#1E3A8A] text-sm font-medium mb-6">
                <Microscope className="w-4 h-4" />
                Research Methodology
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-6" style={{ color: '#991B1B' }}>
                Research Overview
              </h1>
              <p className="text-lg text-[#1E3A8A] leading-relaxed">
                The Zodiac Workforce Intelligence Institute advances the science of workforce 
                intelligence through rigorous research, validated instruments, and evidence-based methodologies.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Research Methodology */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl font-bold mb-4" style={{ color: '#991B1B' }}>
              Research Methodology
            </h2>
            <p className="text-lg text-[#1E3A8A] max-w-2xl mx-auto">
              Our research combines quantitative analytics with qualitative insights for comprehensive understanding.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {methodology.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full border-[#E5E7EB] bg-white">
                  <CardContent className="p-6">
                    <div className="w-10 h-10 rounded-lg bg-[#1E3A8A] text-white flex items-center justify-center font-mono font-bold text-lg mb-4">
                      {index + 1}
                    </div>
                    <h3 className="font-heading text-lg font-semibold mb-2" style={{ color: '#991B1B' }}>
                      {item.title}
                    </h3>
                    <p className="text-sm text-[#1E3A8A]">{item.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Focus Areas */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl font-bold mb-4" style={{ color: '#991B1B' }}>
              Research Focus Areas
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {focusAreas.map((area, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full border-[#E5E7EB]">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-[#1E3A8A]/10 flex items-center justify-center flex-shrink-0">
                        <area.icon className="w-5 h-5 text-[#1E3A8A]" />
                      </div>
                      <div>
                        <h3 className="font-heading text-base font-semibold mb-1" style={{ color: '#991B1B' }}>
                          {area.title}
                        </h3>
                        <p className="text-sm text-[#1E3A8A]">{area.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 border-t border-[#E5E7EB]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl font-bold mb-4" style={{ color: '#991B1B' }}>
            Explore Our Research
          </h2>
          <p className="text-lg text-[#1E3A8A] mb-8">
            Access published research, reports, and workforce intelligence analysis.
          </p>
          <Link to="/research/reports">
            <Button size="lg" className="bg-[#991B1B] text-white hover:bg-[#7F1D1D] h-12 px-8">
              View Reports
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
